import { Dispatch, useEffect, useState } from "react";

import { useSocket } from "components";
import { Channel } from "pusher-js";
import { IAction, ISocketService, useDealActions, useDispatch, useUserIdSelector } from "store";
import { DealAction } from "store/actions";
import {
  DealFormAnswer,
  DealFormAnswerRequestSocket,
  PublishDealFormAnswerSockets,
  UpdateDealFormQuestionSocketPayload,
  UploadFileToDealFormQuestionSockets
} from "store/types";

import { useDealParamsHooks } from "..";

interface IUseSurveySocketHook {
  runSubscriptions: () => void;
}

const questionEventNames: string[] = ["question.created", "question.updated", "question.deleted"];
const answerEventNames: string[] = ["answer.published", "AI-answer.published"];

export function useSurveySocketHook(): IUseSurveySocketHook {
  const { getFormAnswerAction, setRefreshDealForm } = useDealActions();

  const [needSubscribe, setNeedSubscribe] = useState<boolean>(false);
  const [answersChannel, setAnswersChannel] = useState<Channel | null>(null);
  const [filesChannel, setFilesChannel] = useState<Channel | null>(null);
  const [surveyFilesChannel, setSurveyFilesChannel] = useState<Channel | null>(null);
  const [deleteFilesChannel, setDeleteFilesChannel] = useState<Channel | null>(null);
  const [questionChannel, setQuestionChannel] = useState<Channel | null>(null);

  const socket: ISocketService = useSocket();
  const { dealId, formId } = useDealParamsHooks();
  const dispatch: Dispatch<IAction> = useDispatch();
  const userId: number | undefined = useUserIdSelector();

  // Subscribe
  useEffect(() => {
    if (needSubscribe) {
      setAnswersChannel(socket.subscribe(`private-answers-deal-${dealId}-form-${formId}`));
      console.log("[Subscribe for Survey]");

      setFilesChannel(socket.subscribe(`private-files-deal-${dealId}-form-${formId}-user-${userId}`));
      console.log("[Subscribe for Files of Questions]");

      setQuestionChannel(socket.subscribe(`private-deal-${dealId}-form-${formId}-question`));
      console.log("[Subscribe for Questions]");
    }
  }, [needSubscribe, formId]);

  useEffect(() => {
    if (filesChannel) {
      setSurveyFilesChannel(filesChannel);
      setDeleteFilesChannel(filesChannel);
    }
  }, [filesChannel]);

  // Unsubscribe
  useEffect(() => {
    return () => {
      socket.unsubscribe(`private-answers-deal-${dealId}-form-${formId}`);
      socket.unsubscribe(`private-files-deal-${dealId}-form-${formId}-user-${userId}`);
      socket.unsubscribe(`private-deal-${dealId}-form-${formId}-question`);
      console.log("[Unsubscribe from Survey]");
      console.log("[Unsubscribe from Question]");
      console.log("[Unsubscribe from Files of Questions]");
    };
  }, [formId]);

  useEffect(() => {
    if (answersChannel) {
      answerEventNames.forEach((eventName: string) => {
        answersChannel.bind(eventName, (payload: PublishDealFormAnswerSockets) => {
          if (eventName === answerEventNames[1] && payload.dealId && payload.formId === formId) {
            //AI answers for form ready, so we need to show refresh form bar
            console.log("[AI filled out the form]");
            setRefreshDealForm(true);
            return;
          }
          console.log("[New Publish Answer]: ", payload);
          if (userId === (payload.answer as DealFormAnswer)?.user_id) {
            console.log("[This is yours published answer]");
            return;
          }
          if (eventName === answerEventNames[0] && (payload.answer as DealFormAnswerRequestSocket).need_to_refresh) {
            getFormAnswerAction({
              formId,
              answerId: (payload.answer as DealFormAnswerRequestSocket).id
            });
            return;
          }
          dispatch({ type: DealAction.PublishDealFormAnswerSocket, payload });
        });
      });
    }
  }, [answersChannel, answerEventNames, formId]);

  useEffect(() => {
    if (filesChannel) {
      filesChannel.bind("files.answerUpload", ({ file }: UploadFileToDealFormQuestionSockets) => {
        if (userId === file.user_id) {
          console.log("[This is yours uploaded file]");
          return;
        }
        console.log("[New File Uploaded to Question]: ", file);
        dispatch({
          type: DealAction.HandleFileSuccess,
          payload: { item: file, event: "add" }
        });
      });
    }
  }, [filesChannel]);

  useEffect(() => {
    if (surveyFilesChannel) {
      surveyFilesChannel.bind("files.formUpload", ({ file }: UploadFileToDealFormQuestionSockets) => {
        if (userId === file.user_id) {
          console.log("[This is yours uploaded file]");
          return;
        }
        console.log("[New File Uploaded to Survey]: ", file);
        dispatch({
          type: DealAction.HandleFileSuccess,
          payload: { item: file, event: "add" }
        });
      });
    }
  }, [surveyFilesChannel]);

  useEffect(() => {
    if (deleteFilesChannel) {
      deleteFilesChannel.bind("files.deleted", ({ file }: UploadFileToDealFormQuestionSockets) => {
        if (file.upload_type === "question") {
          console.log("File Deleted from Survey]: ", file);
          dispatch({
            type: DealAction.HandleFileSuccess,
            payload: { item: file, event: "delete" }
          });
        }
        if (file.upload_type === "form") {
          console.log("File Deleted from Survey]: ", file);
          dispatch({
            type: DealAction.HandleFileSuccess,
            payload: { item: file, event: "delete" }
          });
        }
      });
    }
  }, [deleteFilesChannel]);

  useEffect(() => {
    if (questionChannel) {
      questionEventNames.forEach((eventName: string) => {
        questionChannel.bind(eventName, (payload: UpdateDealFormQuestionSocketPayload) => {
          if (userId === payload.userId) {
            console.log("[This is yours change in question]");
            return;
          }
          console.log("[Questions updated]: ", payload);
          setRefreshDealForm(true);
        });
      });
    }
  }, [questionChannel, questionEventNames]);

  return {
    runSubscriptions: () => setNeedSubscribe(true)
  };
}
